import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  Stack,
  Divider,
  LinearProgress,
} from '@mui/material'
import { FiberManualRecord, LocationOn, Speed } from '@mui/icons-material'
import { PageHeader } from '../../components/PageHeader'
import { useTwinDevices } from '../../hooks/useDigitalTwin'
import { useDevices, useSensorDefinitions } from '../../hooks/useDevices'
import { formatDistanceToNow } from 'date-fns'

export default function DigitalTwinPage() {
  const { t } = useTranslation()
  const { data: twins = [], isLoading, isFetching } = useTwinDevices()
  const { data: devices = [] } = useDevices()
  const { data: sensorDefs = [] } = useSensorDefinitions()

  // Build lookup: device_id -> { name, device_type, vehicle_type }
  const deviceMap = useMemo(() => {
    const map = new Map<string, { name: string; device_type?: string; vehicle_type?: string }>()
    devices.forEach((d) => map.set(d.id, { name: d.name, device_type: d.device_type, vehicle_type: d.vehicle_type }))
    return map
  }, [devices])

  // Build lookup: sensor key -> { display_name, unit }
  const sensorLabels = useMemo(() => {
    const map = new Map<string, { label: string; unit: string }>()
    sensorDefs.forEach((s) => map.set(s.name, { label: s.name, unit: s.unit }))
    return map
  }, [sensorDefs])

  return (
    <Box>
      <PageHeader
        title={t('twin.title')}
        subtitle={t('twin.subtitle')}
        action={
          isFetching && <CircularProgress size={20} />
        }
      />

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
          <CircularProgress />
        </Box>
      ) : twins.length === 0 ? (
        <Alert severity="info">
          {t('twin.noDeviceTwinData')}
        </Alert>
      ) : (
        <Grid container spacing={2}>
          {twins.map((twin) => {
            const sensors = twin.sensor_data ?? {}
            const sensorKeys = Object.keys(sensors)
            const meta = deviceMap.get(twin.device_id)
            return (
              <Grid item xs={12} sm={6} md={4} key={twin.device_id}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FiberManualRecord
                          sx={{
                            fontSize: 12,
                            color: twin.status === 'online' ? 'success.main'
                              : twin.status === 'idle' ? 'warning.main'
                              : 'text.disabled',
                          }}
                        />
                        <Box>
                          <Typography variant="body1" fontWeight={600}>
                            {meta?.name ?? twin.device_id.slice(0, 12)}
                          </Typography>
                          {(meta?.device_type || meta?.vehicle_type) && (
                            <Typography variant="caption" color="text.secondary">
                              {[meta.device_type, meta.vehicle_type].filter(Boolean).join(' · ')}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                      <Chip
                        label={twin.status}
                        size="small"
                        color={twin.status === 'online' ? 'success' : twin.status === 'idle' ? 'warning' : 'default'}
                      />
                    </Box>

                    <Typography variant="caption" color="text.secondary">
                      {t('device.lastSeen')}:{' '}
                      {twin.last_seen_at
                        ? formatDistanceToNow(new Date(twin.last_seen_at), { addSuffix: true })
                        : '—'}
                    </Typography>

                    {twin.last_latitude != null && twin.last_longitude != null && (
                      <>
                        <Divider sx={{ my: 1.5 }} />
                        <Box sx={{ display: 'flex', gap: 1.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <LocationOn sx={{ fontSize: 16, color: 'primary.main' }} />
                            <Typography variant="caption">
                              {twin.last_latitude.toFixed(4)},{' '}
                              {twin.last_longitude.toFixed(4)}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Speed sx={{ fontSize: 16, color: 'secondary.main' }} />
                            <Typography variant="caption">
                              {twin.last_speed ?? 0} km/h
                            </Typography>
                          </Box>
                        </Box>
                      </>
                    )}

                    {twin.health_score != null && (
                      <>
                        <Divider sx={{ my: 1.5 }} />
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="caption" color="text.secondary">{t('twin.health')}</Typography>
                          <LinearProgress
                            variant="determinate"
                            value={twin.health_score}
                            sx={{ flex: 1, height: 6, borderRadius: 3 }}
                            color={twin.health_score >= 70 ? 'success' : twin.health_score >= 40 ? 'warning' : 'error'}
                          />
                          <Typography variant="caption" fontWeight={600}>{twin.health_score}%</Typography>
                        </Box>
                      </>
                    )}

                    {sensorKeys.length > 0 && (
                      <>
                        <Divider sx={{ my: 1.5 }} />
                        <Stack spacing={0.5}>
                          {sensorKeys.map((key) => {
                            const def = sensorLabels.get(key)
                            return (
                              <Box key={key} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="caption" color="text.secondary">
                                  {def?.label ?? key}
                                </Typography>
                                <Typography variant="caption" fontWeight={600}>
                                  {String(sensors[key])}{def?.unit ? ` ${def.unit}` : ''}
                                </Typography>
                              </Box>
                            )
                          })}
                        </Stack>
                      </>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            )
          })}
        </Grid>
      )}
    </Box>
  )
}
